import React, { useState, useEffect, useContext } from 'react';
import { Box, Button, Typography, Paper, styled, CircularProgress } from '@mui/material';
import { OrgContext } from '../contexts/OrgContext';
import { useNavigate } from 'react-router-dom';
import { bulkCreateInventory } from '../services/inventoryService';
import { getProductsByOrgId } from '../services/productService';
import Papa from 'papaparse';
import TanStackTable from '../components/TanStackTable';
import BackButton from '../components/BackButton';

const HiddenInput = styled('input')({
  display: 'none',
});

const InventoryBulkImport = () => {
  const { orgId } = useContext(OrgContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsData = await getProductsByOrgId(orgId);
        setProducts(productsData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Error fetching product data. Please try again.');
        setLoading(false);
      }
    };

    if (orgId) {
      fetchProducts();
    }
  }, [orgId]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDownloadTemplate = () => {
    // Create CSV header
    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'product_id,quantity_on_hand,minimum_stock_level,maximum_stock_level,reorder_point,default_order_quantity,last_unit_cost,last_unit_price\n';
    
    // Add product IDs to the template
    products.forEach(product => {
      csvContent += `${product.id},0,10,100,20,5,0,0\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'inventory_template.csv');
    document.body.appendChild(link);
    link.click();
  };

  const handleUpload = () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    setError(null);

    Papa.parse(file, {
      header: true,
      complete: async (parseResults) => {
        const inventoryItems = parseResults.data.map((item, index) => ({
          product_id: parseInt(item.product_id, 10),
          quantity_on_hand: parseInt(item.quantity_on_hand, 10) || 0,
          minimum_stock_level: parseInt(item.minimum_stock_level, 10) || null,
          maximum_stock_level: parseInt(item.maximum_stock_level, 10) || null,
          reorder_point: parseInt(item.reorder_point, 10) || null,
          default_order_quantity: parseInt(item.default_order_quantity, 10) || null,
          last_unit_cost: parseFloat(item.last_unit_cost) || null,
          last_unit_price: parseFloat(item.last_unit_price) || null,
          org_id: parseInt(orgId, 10),
          row: index + 2, // Add row number for error tracking (1 for header, so start from 2)
        }));

        try {
          const response = await bulkCreateInventory(inventoryItems);
          setResults(response);
        } catch (err) {
          setError('Error uploading inventory items.');
          console.error(err);
        }
      },
      error: (err, file, inputElem, reason) => {
        setError(`Error parsing CSV file at row ${err.row}: ${reason}`);
        console.error(err);
      },
    });
  };

  const handleDone = () => {
    navigate(`/${orgId}/inventory`);
  };

  // Add product details to results to show product names instead of just IDs
  const enhanceResultsWithProductDetails = (results) => {
    if (!results) return null;

    const productMap = {};
    products.forEach(product => {
      productMap[product.id] = product;
    });

    const enhancedSuccesses = results.successes.map(success => {
      const product = productMap[success.product?.id];
      return {
        ...success,
        product_name: product ? product.product_name : 'Unknown Product',
        product_id: success.product?.id || 'Unknown'
      };
    });

    const enhancedFailures = results.failures.map(failure => {
      const product = productMap[failure.dto.product_id];
      return {
        ...failure.dto,
        error: failure.error,
        row: failure.dto.row,
        product_name: product ? product.product_name : 'Unknown Product'
      };
    });

    return {
      successes: enhancedSuccesses,
      failures: enhancedFailures
    };
  };

  const enhancedResults = enhanceResultsWithProductDetails(results);

  return (
    <Paper sx={{ p: 2, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
      <BackButton />
      <Typography variant="h5" gutterBottom>
        Bulk Import Inventory
      </Typography>
      <Typography variant="body1" gutterBottom>
        Follow the instructions below to bulk import inventory items:
      </Typography>
      <Typography variant="body2" gutterBottom>
        1. Download the template CSV file with all your existing products.
      </Typography>
      <Typography variant="body2" gutterBottom>
        2. Fill in the inventory details for each product in the CSV file.
      </Typography>
      <Typography variant="body2" gutterBottom>
        3. Required fields: product_id, quantity_on_hand.
      </Typography>
      <Typography variant="body2" gutterBottom>
        4. Optional fields: minimum_stock_level, maximum_stock_level, reorder_point, default_order_quantity, last_unit_cost, last_unit_price.
      </Typography>
      <Typography variant="body2" gutterBottom>
        5. Upload the completed CSV file and start bulk import.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Button 
            variant="contained" 
            onClick={handleDownloadTemplate} 
            sx={{ my: 2, mr: 2 }}
            disabled={products.length === 0}
          >
            Download Template CSV
          </Button>

          <label htmlFor="contained-button-file">
            <HiddenInput
              accept=".csv"
              id="contained-button-file"
              type="file"
              onChange={handleFileChange}
            />
            <Button variant="contained" component="span">
              Select CSV File
            </Button>
          </label>

          {file && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Selected File: {file.name}
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handleUpload}>
              Start Bulk Import
            </Button>
          </Box>
        </>
      )}

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {enhancedResults && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Bulk Import Results
          </Typography>
          <Typography sx={{ mb: 3, color: "#ddd" }} variant="body2" gutterBottom>
            Scroll down to see the successful and failed imports.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Successful Imports ({enhancedResults.successes.length})
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TanStackTable
              data={enhancedResults.successes || []}
              columns={[
                { header: 'ID', accessorKey: 'id' },
                { header: 'Product ID', accessorKey: 'product_id' },
                { header: 'Product Name', accessorKey: 'product_name' },
                { header: 'Quantity', accessorKey: 'quantity_on_hand' },
                { header: 'Min Stock', accessorKey: 'minimum_stock_level' },
                { header: 'Max Stock', accessorKey: 'maximum_stock_level' },
                { header: 'Reorder Point', accessorKey: 'reorder_point' },
              ]}
            />
          </Box>
          <Typography variant="body2" gutterBottom>
            Failed Imports ({enhancedResults.failures.length})
          </Typography>
          <Box>
            <TanStackTable
              data={enhancedResults.failures || []}
              columns={[
                { header: 'Row', accessorKey: 'row' },
                { header: 'Product ID', accessorKey: 'product_id' },
                { header: 'Product Name', accessorKey: 'product_name' },
                { header: 'Quantity', accessorKey: 'quantity_on_hand' },
                { header: 'Error', accessorKey: 'error' },
              ]}
            />
          </Box>
          <Button variant="contained" onClick={handleDone} sx={{ mt: 2 }}>
            Done
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default InventoryBulkImport;
